import { Alert, Box, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { AlertPresets } from "../../../../../mantine/mantinePresets";
import {
  MissingDataTypes,
  type VollmachtDetail
} from "../../../../../utils/backend-types";
import { MarkDoneActionButton } from "../../../components/MarkDoneActionButton";
import { AssignmentType } from "../../OnboardingWizard.constants";
import { DownloadTemplateButton } from "./Buttons/DownloadTemplateButton";
import { UploadVollmachtPersonMenu } from "./Buttons/UploadVollmachtPersonMenu";
import { useVollmachtVermarkterUpload } from "./hooks/useVollmachtVermarkterUpload";
import { VollmachtSection } from "./VollmachtSection";
import { VollmachtVermarkterConfirmation } from "./VollmachtVermarkterConfirmation/VollmachtVermarkterConfirmation";

interface VollmachtVermarkterProps {
  isDisabled?: boolean;
  phase: VollmachtDetail;
  siteId: number;
  updateSiteWizard: (type: AssignmentType) => Promise<void>;
}

export const vollmachtVermarkterId = "vollmacht-vermarkter";

function VollmachtVermarkter({
  isDisabled,
  phase,
  siteId,
  updateSiteWizard
}: VollmachtVermarkterProps) {
  const { missingData, done, hasMissingDocuments } = phase;
  const {
    confirmationData,
    confirmVollmachtVermarkterMutation,
    generateAndDownloadVollmachtVermarkterMutation,
    personsWithEmAndRedispatch,
    uploadVollmachtVermarkterMutation,
    handleCloseConfirmation,
    handleConfirmVollmacht,
    handleFilesSelected
  } = useVollmachtVermarkterUpload(siteId);

  const missingFiles = missingData.filter(
    (item) => item.missingDataType === MissingDataTypes.POWER_OF_ATTORNEY_DATA
  );
  const missingDataWithoutFiles = missingData.filter(
    (item) => item.missingDataType !== MissingDataTypes.POWER_OF_ATTORNEY_DATA
  );
  const hasMissingData = missingDataWithoutFiles.length > 0;

  const submitMutation = useMutation({
    mutationFn: () => updateSiteWizard(AssignmentType.VollmachtVermarkter)
  });

  return (
    <>
      <VollmachtSection
        actions={
          <>
            <DownloadTemplateButton
              onClick={() =>
                generateAndDownloadVollmachtVermarkterMutation.mutate()
              }
            />
            <UploadVollmachtPersonMenu
              persons={personsWithEmAndRedispatch}
              onFilesSelected={handleFilesSelected}
            />
            {!hasMissingDocuments && (
              <MarkDoneActionButton
                disabled={submitMutation.isPending}
                mb="xs"
                mt={0}
                onClick={() => submitMutation.mutate()}
              />
            )}
          </>
        }
        done={done}
        hasMissingData={hasMissingData}
        isDisabled={isDisabled}
        isLoading={
          generateAndDownloadVollmachtVermarkterMutation.isPending ||
          uploadVollmachtVermarkterMutation.isPending
        }
        missingDataRoute={vollmachtVermarkterId}
        objectName="Vermarkter"
        trailingContent={
          confirmationData && (
            <VollmachtVermarkterConfirmation
              isOpen={true}
              isSubmitting={confirmVollmachtVermarkterMutation.isPending}
              uploadResponse={confirmationData}
              onClose={handleCloseConfirmation}
              onConfirmDataCorrect={() => handleConfirmVollmacht(true)}
              onDiscardFile={() => handleConfirmVollmacht(false)}
            />
          )
        }
      />
      {missingFiles.length > 0 && (
        <Alert
          color={AlertPresets.error.color}
          icon={AlertPresets.error.icon}
          mt="xs"
        >
          <Text fz="xs">
            {`Fehlende Vermarktervollmachten für ${missingFiles.length} Erzeuger`}

            <Tooltip
              label={new Intl.ListFormat("de", {
                style: "long",
                type: "conjunction"
              }).format(missingFiles.map((file) => file.name))}
              position="bottom"
            >
              <Box
                component="span"
                ml={4}
                style={{
                  display: "inline-flex",
                  verticalAlign: "middle"
                }}
              >
                <IconInfoCircle
                  color="var(--mantine-color-primary-filled)"
                  size="1rem"
                />
              </Box>
            </Tooltip>
          </Text>
        </Alert>
      )}
    </>
  );
}

export { VollmachtVermarkter };
